import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import type { ModelOutputRow, EvalDataset } from '../../types';
import type { PromptType } from '../../types/settings';
import { PROMPT_TYPE_GROUPED_OPTIONS } from '../../types/promptTypeLabels';
import { format } from 'date-fns';
import { LatexText } from '@shared-ui/latex';
import { DataTable, SearchFilterBar, Pagination, type ColumnDef, type SortState, type RowMenuItem } from '@shared-ui/components';
import { RunStatusCell } from '@shared-ui/operation-detail';
import { withReturnTo } from '@shared-ui/navigation';
import './ModelData.css';

const SUBJECT_OPTIONS = [
  { value: 'maths', label: 'Maths' },
  { value: 'physics', label: 'Physics' },
  { value: 'chemistry', label: 'Chemistry' },
  { value: 'biology', label: 'Biology' },
];

const SOURCE_OPTIONS = [
  { value: 'production', label: 'Production' },
  { value: 'eval', label: 'Eval' },
  { value: 'replay', label: 'Admin re-runs' },
];

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '3d', label: '< 3 days' },
  { value: '7d', label: '< 7 days' },
  { value: 'all', label: 'All' },
];

const getSinceDate = (range: string): string | undefined => {
  if (range === 'all') return undefined;
  const now = new Date();
  if (range === 'today') now.setHours(0, 0, 0, 0);
  else if (range === '3d') now.setDate(now.getDate() - 3);
  else if (range === '7d') now.setDate(now.getDate() - 7);
  return now.toISOString();
};

type SortField = 'generatedAt' | 'latencyMs' | 'cost';

// Scroll-position store, keyed by `pathname + search`. Lets back-navigation
// from a detail page land back on the same row, not the top of the list.
const SCROLL_KEY_PREFIX = 'admin:model-data-scroll:';

export function ModelData() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL-derived filter state ─────────────────────────────────────────
  // Filters, page, and sort live in the URL so back-navigation from the
  // detail page restores them automatically (browser keeps the search string).
  const preselectedDatasetId = searchParams.get('datasetId') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const source = (searchParams.get('source') || '') as '' | 'production' | 'eval' | 'replay';
  const subjectFilter = searchParams.get('subject') || '';
  const promptTypeFilter = (searchParams.get('promptType') || '') as '' | PromptType;
  const modelFilter = searchParams.get('model') || '';
  const emailFilter = searchParams.get('email') || '';
  // Set from a row's right-click menu, not the filter bar.
  const userIdFilter = searchParams.get('userId') || '';
  const submissionIdFilter = searchParams.get('submissionId') || '';
  const dateRange = searchParams.get('dateRange') || '7d';
  const sortBy = (searchParams.get('sortBy') || 'generatedAt') as SortField;
  const sortOrder = (searchParams.get('sortOrder') || 'desc') as 'asc' | 'desc';
  const escalatedOnly = searchParams.get('escalated') === '1';
  const failedOnly = searchParams.get('failed') === '1';

  // Ephemeral UI state — doesn't need URL persistence.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [targetDatasetId, setTargetDatasetId] = useState(preselectedDatasetId);
  const pageSize = 20;

  // Merge URL params with the existing query string and write back. Empty
  // string deletes the param; `page` always resets to 1 when a filter
  // changes (callers pass `page: undefined` or omit it).
  const updateParams = useCallback((updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(updates)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  // Fetch model stats
  const { data: stats } = useQuery({
    queryKey: ['modelStats'],
    queryFn: () => adminApi.getModelStats(),
  });

  // Fetch model outputs (one row per output, server-side enriched)
  const { data, isLoading, error } = useQuery({
    queryKey: ['modelOutputs', page, source, promptTypeFilter, subjectFilter, modelFilter, emailFilter, userIdFilter, submissionIdFilter, dateRange, sortBy, sortOrder, failedOnly],
    queryFn: () => adminApi.getModelOutputs({
      page,
      pageSize,
      source: source || undefined,
      promptType: promptTypeFilter || undefined,
      subject: subjectFilter || undefined,
      model: modelFilter || undefined,
      email: emailFilter || undefined,
      userId: userIdFilter || undefined,
      submissionId: submissionIdFilter || undefined,
      sinceDate: getSinceDate(dateRange),
      failedOnly: failedOnly || undefined,
      sortBy,
      sortOrder,
    }),
    // A call's row exists from its start; refresh while any shown row is still
    // in flight, so it turns into its finished self without a manual refresh.
    refetchInterval: q => (q.state.data?.outputs?.some(o => o.status === 'running') ? 5000 : false),
  });

  // Fetch datasets for add to dataset modal
  const { data: datasetsData } = useQuery({
    queryKey: ['evalDatasets'],
    queryFn: () => adminApi.getEvalDatasets(),
  });

  // Import to dataset mutation (uses inputIds from selected outputs)
  const importMutation = useMutation({
    mutationFn: (params: { datasetId: string; inputIds: string[] }) =>
      adminApi.importDatasetItems(params.datasetId, { inputIds: params.inputIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalDatasets'] });
      setShowAddModal(false);
      setSelectedIds(new Set());
      setTargetDatasetId('');
    },
  });

  const allOutputs = data?.outputs || [];
  // Client-side filter for "escalation fired" — picks rows whose tier-routing
  // trace has at least one escalation entry. Backend currently has no SQL
  // index for this; if the volume grows, push the filter into getModelOutputs.
  const outputs = useMemo(() => {
    if (!escalatedOnly) return allOutputs;
    return allOutputs.filter(r => (r.parameters.actual?.tierRouting?.escalations?.length ?? 0) > 0);
  }, [allOutputs, escalatedOnly]);

  // Right-click a row to narrow the list to its user or its operation (the
  // submission or job it ran for), as on the operations lists.
  const rowMenu = (row: ModelOutputRow): RowMenuItem[] => {
    const items: RowMenuItem[] = [];
    if (row.userId) {
      items.push({
        key: 'user',
        label: `Filter to ${row.userName || row.userLogin || row.userEmail || 'this user'}`,
        disabled: userIdFilter === row.userId,
        onSelect: () => updateParams({ userId: row.userId, page: '1' }),
      });
    }
    if (row.submissionId) {
      items.push({
        key: 'operation',
        label: 'Filter to this operation',
        disabled: submissionIdFilter === row.submissionId,
        onSelect: () => updateParams({ submissionId: row.submissionId, page: '1' }),
      });
    }
    if (items.length > 0) {
      items.push({
        key: 'clear',
        label: 'Clear user and operation filters',
        separatorBefore: true,
        disabled: !userIdFilter && !submissionIdFilter,
        onSelect: () => updateParams({ userId: '', submissionId: '', page: '1' }),
      });
    }
    return items;
  };

  const handleSortChange = (next: SortState) => {
    const nextSortBy: SortField =
      next.sortBy === 'latencyMs' ? 'latencyMs'
      : next.sortBy === 'cost' ? 'cost'
      : 'generatedAt';
    updateParams({ sortBy: nextSortBy, sortOrder: next.sortOrder, page: '1' });
  };

  // Selection handlers — selected IDs are output IDs, but we track inputIds for dataset import
  const handleSelectAll = () => {
    if (selectedIds.size === outputs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(outputs.map(o => o.id)));
    }
  };

  const handleSelectOne = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const allSelected = outputs.length > 0 && selectedIds.size === outputs.length;
  const isExtractSteps = promptTypeFilter === 'extract_steps';

  const columns = useMemo<ColumnDef<ModelOutputRow>[]>(() => {
    const checkboxCol: ColumnDef<ModelOutputRow> = {
      key: 'select',
      className: 'dt-checkbox-col',
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={handleSelectAll}
          onClick={(e) => e.stopPropagation()}
          title="Select all"
        />
      ),
      render: (row) => (
        <span onClick={(e) => handleSelectOne(row.id, e)}>
          <input
            type="checkbox"
            checked={selectedIds.has(row.id)}
            onChange={() => {}}
          />
        </span>
      ),
    };

    const dateCol: ColumnDef<ModelOutputRow> = {
      key: 'generatedAt',
      header: 'Date',
      sortable: true,
      render: (row) => format(new Date(row.generatedAt), 'MMM d, HH:mm'),
    };

    const userCol: ColumnDef<ModelOutputRow> = {
      key: 'user',
      header: 'User',
      render: (row) => (
        <div className="user-cell">
          {/* The user's name. Only a user with no name stored falls back to
              their login id (roster accounts have no email), email, or id. */}
          {(() => {
            const who = row.userName || row.userLogin || row.userEmail || (row.userId ? row.userId.substring(0, 8) + '…' : '-');
            // At most 20 characters; the whole name on hover.
            return <span className="user-name" title={who}>{who.length > 20 ? `${who.slice(0, 20)}…` : who}</span>;
          })()}
        </div>
      ),
    };

    const subjectCol: ColumnDef<ModelOutputRow> = {
      key: 'subject',
      header: 'Subject',
      render: (row) => <span className="subject-badge">{row.subject || '-'}</span>,
    };

    const questionCol: ColumnDef<ModelOutputRow> = {
      key: 'question',
      header: 'Question',
      render: (row) => (
        <span className="question-preview" title={row.questionText}>
          <LatexText text={row.questionText || ''} truncate={40} />
        </span>
      ),
    };

    const promptCol: ColumnDef<ModelOutputRow> = {
      key: 'prompt',
      header: 'Prompt',
      render: (row) => (
        <span className="prompt-name">
          {row.promptName || '-'}
          {row.promptType ? <span className="prompt-type-inline"> ({row.promptType})</span> : null}
        </span>
      ),
    };

    const modelCol: ColumnDef<ModelOutputRow> = {
      key: 'model',
      header: 'Model',
      render: (row) => <span className="provider-badge">{row.parameters.model}</span>,
    };

    const costCol: ColumnDef<ModelOutputRow> = {
      key: 'cost',
      header: 'Cost',
      sortable: true,
      render: (row) => (
        <span className="cost-cell">{row.cost > 0 ? `$${row.cost.toFixed(4)}` : '-'}</span>
      ),
    };

    const latencyCol: ColumnDef<ModelOutputRow> = {
      key: 'latencyMs',
      header: 'Latency',
      sortable: true,
      render: (row) => (
        <span className="latency-cell">{row.latencyMs > 0 ? `${(row.latencyMs / 1000).toFixed(1)}s` : '-'}</span>
      ),
    };

    const pipelineCol: ColumnDef<ModelOutputRow> = {
      key: 'pipeline',
      header: 'Pipeline',
      render: (row) => (
        // A re-run is an admin's comparison, not a pipeline step: say so.
        row.source === 'replay'
          ? <span className="tag" style={{ fontSize: '11px', background: '#ede9fe', color: '#6d28d9' }} title="Admin re-run with another model — not part of any analysis">admin re-run</span>
          : <span className="tag" style={{ fontSize: '11px' }}>{row.pipelineName || 'direct'}</span>
      ),
    };

    // Success/error at a glance: green "success" or the short error code with the
    // full string on hover. Same cell as the operation-detail run tables.
    const statusCol: ColumnDef<ModelOutputRow> = {
      key: 'status',
      header: 'Status',
      render: (row) => <RunStatusCell run={row} />,
    };

    if (isExtractSteps) {
      return [checkboxCol, dateCol, userCol, subjectCol, promptCol, modelCol, costCol, latencyCol, statusCol];
    }
    return [checkboxCol, dateCol, userCol, subjectCol, questionCol, pipelineCol, promptCol, modelCol, costCol, latencyCol, statusCol];
  }, [allSelected, selectedIds, outputs, isExtractSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get unique inputIds from selected output rows (for dataset import)
  const selectedInputIds = useMemo(() => {
    const inputIds = new Set<string>();
    for (const output of outputs) {
      if (selectedIds.has(output.id)) {
        inputIds.add(output.inputId);
      }
    }
    return [...inputIds];
  }, [selectedIds, outputs]);

  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['modelOutputs'] });
    queryClient.invalidateQueries({ queryKey: ['modelStats'] });
  }, [queryClient]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  // ── Scroll save / restore ────────────────────────────────────────────
  // sessionStorage-backed, keyed by URL. The Layout's `.sidebar-layout-content`
  // is the scroll container (not window). We save on scroll (rAF-throttled)
  // and restore once after the row data finishes loading on a fresh visit.
  const scrollKey = SCROLL_KEY_PREFIX + location.pathname + location.search;
  const restoredRef = useRef(false);
  useEffect(() => { restoredRef.current = false; }, [scrollKey]);

  useEffect(() => {
    const el = document.querySelector('.sidebar-layout-content') as HTMLElement | null;
    if (!el) return;
    let pending = false;
    const onScroll = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        sessionStorage.setItem(scrollKey, String(el.scrollTop));
        pending = false;
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollKey]);

  useEffect(() => {
    if (isLoading || restoredRef.current) return;
    restoredRef.current = true;
    const el = document.querySelector('.sidebar-layout-content') as HTMLElement | null;
    if (!el) return;
    const saved = sessionStorage.getItem(scrollKey);
    if (saved) el.scrollTop = parseInt(saved, 10);
  }, [isLoading, scrollKey]);

  return (
    <div className="model-data-page">
      <div className="page-header">
        <h1>Model Runs</h1>
        <button className="refresh-btn" onClick={refreshData} disabled={isLoading} title="Refresh data">
          &#x21bb; Refresh
        </button>
      </div>

      {/* Dataset import banner */}
      {preselectedDatasetId && (
        <div className="dataset-import-banner">
          Selecting items for dataset: <strong>{datasetsData?.datasets.find((ds: EvalDataset) => ds.id === preselectedDatasetId)?.name || preselectedDatasetId}</strong>
          — Select model runs below and click "Add to Dataset"
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="stat-cards stat-cards--auto">
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{stats.totalOutputs}</div>
            <div className="stat-card-label">Total Outputs</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{stats.totalInputs}</div>
            <div className="stat-card-label">Total Inputs</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{stats.productionInputs}</div>
            <div className="stat-card-label">Production</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{stats.evalInputs}</div>
            <div className="stat-card-label">Eval</div>
          </div>
          <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{stats.recentInputs}</div>
            <div className="stat-card-label">Last 24h</div>
          </div>
        </div>
      )}

      {/* Add-to-Dataset action — own row above the filter bar, right-aligned. */}
      <div className="selection-actions model-data-selection-row">
        {selectedIds.size > 0 && (
          <span className="selection-count">{selectedIds.size} selected</span>
        )}
        <button
          className="action-btn primary model-data-add-btn"
          onClick={() => setShowAddModal(true)}
          disabled={selectedIds.size === 0}
        >
          Add to Dataset
        </button>
      </div>

      {/* Filters Bar */}
      <SearchFilterBar
        filters={[
          { key: 'dateRange', kind: 'select', placeholder: 'Date Range', required: true, options: DATE_RANGE_OPTIONS },
          { key: 'promptType', kind: 'select', placeholder: 'All Types', options: PROMPT_TYPE_GROUPED_OPTIONS },
          { key: 'source', kind: 'select', placeholder: 'All Sources', options: SOURCE_OPTIONS },
          { key: 'subject', kind: 'select', placeholder: 'All Subjects', options: SUBJECT_OPTIONS },
          { key: 'model', kind: 'select', placeholder: 'All Models', options: (stats?.distinctModels || []).map(m => ({ value: m, label: m })) },
          { key: 'email', kind: 'text', placeholder: 'User name, email or login' },
        ]}
        values={{ dateRange, promptType: promptTypeFilter, source, subject: subjectFilter, model: modelFilter, email: emailFilter }}
        onChange={(v) => {
          updateParams({
            dateRange: v.dateRange ?? '7d',
            promptType: v.promptType ?? '',
            source: v.source ?? '',
            subject: v.subject ?? '',
            model: v.model ?? '',
            email: v.email ?? '',
            page: '1',
          });
        }}
        hideFetch
        hideSearch
        trailingActions={data ? (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={data.total}
            onPageChange={(p) => updateParams({ page: String(p) })}
          />
        ) : undefined}
      >
        <label className="escalated-filter" title="Show only runs whose LLM call failed (rate limit, quota, timeout, parse error). Applied server-side, so the count and paging describe every failure — not just this page.">
          <input
            type="checkbox"
            checked={failedOnly}
            onChange={e => updateParams({ failed: e.target.checked ? '1' : '', page: '1' })}
          />
          Failed only
        </label>
        <label className="escalated-filter" title="Show only submissions where analysis tier escalation fired (Step 8). Filter is applied client-side on the current page.">
          <input
            type="checkbox"
            checked={escalatedOnly}
            onChange={e => updateParams({ escalated: e.target.checked ? '1' : '' })}
          />
          Escalated only
        </label>
        {userIdFilter && (
          <span className="row-filter-chip" title={userIdFilter}>
            User: {(() => {
              const r = outputs.find(o => o.userId === userIdFilter);
              return r?.userName || r?.userLogin || r?.userEmail || `…${userIdFilter.slice(-8)}`;
            })()}
            <button onClick={() => updateParams({ userId: '', page: '1' })} aria-label="Clear user filter">×</button>
          </span>
        )}
        {submissionIdFilter && (
          <span className="row-filter-chip" title={submissionIdFilter}>
            Operation: …{submissionIdFilter.slice(-8)}
            <button onClick={() => updateParams({ submissionId: '', page: '1' })} aria-label="Clear operation filter">×</button>
          </span>
        )}
      </SearchFilterBar>

      {error && <div className="error">Error: {(error as Error).message}</div>}

      <DataTable
        columns={columns}
        data={outputs}
        rowKey={(r) => r.id}
        rowMenu={rowMenu}
        onRowClick={(r) => navigate(
          `/model-data/${r.id}`,
          // Stamp returnTo so BackButton uses `navigate(-1)` (preserves filters
          // and page in the URL). Also stash the filtered id list so the
          // detail page can wire ←/→. Falls back to a single-id list when
          // state is lost (e.g. direct link).
          withReturnTo({ label: 'Model Runs' }, { ids: outputs.map(o => o.id) }),
        )}
        rowClassName={(r) => [
          selectedIds.has(r.id) ? 'dt-row--selected' : '',
          // New rows carry structured errorRecords; old rows the legacy errors[].
          (r.errors?.length || r.errorRecords?.length) ? 'dt-row--error' : '',
        ].filter(Boolean).join(' ')}
        sort={{ sortBy, sortOrder }}
        onSortChange={handleSortChange}
        loading={isLoading}
        emptyMessage={`No model outputs found${subjectFilter ? ` for subject "${subjectFilter}"` : ''}${promptTypeFilter ? ` with prompt type "${promptTypeFilter}"` : ''}.`}
      />


      {/* Add to Dataset Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add to Eval Dataset</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Add {selectedInputIds.length} model input(s) from {selectedIds.size} selected output(s) to an evaluation dataset.</p>
              <div className="form-group">
                <label>Select Dataset</label>
                <select
                  value={targetDatasetId}
                  onChange={(e) => setTargetDatasetId(e.target.value)}
                >
                  <option value="">Choose a dataset...</option>
                  {datasetsData?.datasets.map((ds: EvalDataset) => (
                    <option key={ds.id} value={ds.id}>
                      {ds.name} ({ds.metadata.inputCount} items)
                    </option>
                  ))}
                </select>
              </div>
              {importMutation.isError && (
                <div className="error-message">
                  {(importMutation.error as Error).message}
                </div>
              )}
              {importMutation.isSuccess && (
                <div className="success-message">
                  Successfully added items to dataset
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button
                className="submit-btn"
                disabled={!targetDatasetId || importMutation.isPending}
                onClick={() => {
                  importMutation.mutate({
                    datasetId: targetDatasetId,
                    inputIds: selectedInputIds,
                  });
                }}
              >
                {importMutation.isPending ? 'Adding...' : 'Add to Dataset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
